<template>
  <div v-if="pageLoading" class="min-height-100vh flex align-center justify-center">
    <loader-static />
  </div>

  <div v-else :key="renderKey" class="min-height-100">
    <category-brand-header
      v-if="brand && $route.name === 'brand-bio'"
      class="category-brand-header"
      :brand="brand"
      :is-a-brand-user-on-their-own-brand-page="isABrandUserOnTheirOwnBrandPage"
      :is-retailer="user && user.role === 'RETAILER'"
    />

    <template v-if="isMobile">
      <mobile-filter-header :show-filters="() => setMobileFilterVisibility(true)" />
      <mobile-filter-panel
        :total-count="pagination.totalCount"
        :show="showMobileFilter"
        :hide-filters="() => setMobileFilterVisibility(false)"
        :filter-panel-config="getFilterPanelConfig()"
      />
    </template>

    <div class="search-result-page">
      <filter-panel
        v-if="!isMobile"
        class="filter-panel"
        :total-count="pagination.totalCount"
        :filter-panel-config="getFilterPanelConfig()"
      ></filter-panel>
      <div class="page-content">
        <div class="page-header">
          <div class="page-title-row">
            <filter-search-input v-model="searchValue" class="search-input" :class="{ mobile: isMobile }" />
            <sorting-options v-if="!isMobile" />
          </div>
          <h4 v-if="isMobile && pagination.totalCount" class="mobile-total-count color-gray">
            {{ pagination.totalCount }} TOTAL RESULTS
          </h4>
          <filter-badges class="filter-badges" />
        </div>
        <product-tiles :loading="loading" :products="products" :pagination="pagination" :set-page="setPage" />
      </div>
    </div>

    <brand-stockist v-if="brand && brand.id" :brand-id="brand.id" />

    <brand-posts v-if="brand" :brand-id="brand.id" />
  </div>
</template>

<style lang="scss" scoped src="../../assets/sass/searchPage/searchPage.scss"></style>

<style lang="scss" scoped>
@import "@/assets/sass/base/_mixins.scss";
.category-brand-header {
  padding: 40px;

  @include respond-to("small") {
    padding: 16px;
  }
}

.search-input {
  width: calc(25vw - 41px);

  @include respond-to("small") {
    margin-top: 8px;
  }

  &.mobile {
    width: 100%;
  }
}

.page-content {
  padding-top: 0 !important;
}

.filter-panel {
  width: calc(25% - 20px);
  max-width: calc(25% - 20px);
}
</style>

<script>
/* eslint-disable max-lines */
import BrandApi from "./../../services/api/brandApi.js";
import FavoriteApi from "./../../services/api/favoritesApi.js";
import MiscApi from "./../../services/api/misc";
import { mapGetters } from "vuex";
import CategoryBrandHeader from "./categoryBrandHeader/categoryBrandHeader.vue";
import BrandStockist from "./brandStockist/brandStockist.vue";
import BrandPosts from "./brandPosts/brandPosts.vue";
import trackEvent from "@/services/tracking";
import { searchPageMobileStateHelper } from "../../components/mixin/search/searchPageMobileStateHelper";
import { FilterKeys } from "../../services/utils";
import _debounce from "lodash/debounce";
import { algoliaProductHelper } from "../../components/mixin/search/algoliaProductHelper";

const BrandApiService = new BrandApi(),
  MiscApiService = new MiscApi(),
  FavoriteApiService = new FavoriteApi();

export default {
  components: {
    BrandPosts,
    CategoryBrandHeader,
    BrandStockist,
  },
  mixins: [algoliaProductHelper, searchPageMobileStateHelper],
  beforeRouteLeave(to, from, next) {
    if (from.name === "brand-bio") {
      // reset brandForSearch filter when leaving brand-bio
      this.brandForSearch = [];
    }
    if (from.name === "search") {
      this.$eventBus.$emit("close-search");
      next();
    } else {
      next();
    }
  },

  data() {
    return {
      brand: null,
      searchValue: "",
      brandCategories: [],
      brandForSearch: [],
      products: [],
      isSearchActive: false,
      keyword: this.$route.query.keyword,
      isFilterVisible: false,
      favoriteList: [],
      filterOrSortUsed: false,
      filtersOrSortsUsed: new Set(),
      renderKey: 1,
      canCheckFave: this.$session.getUserInfo() === null ? false : this.$session.getUserInfo().role === "RETAILER",
      fullName: null,
      pageLoading: true,
      defaultSorting: "created_at_asc",
      pagination: {
        page: 1,
        limit: 12,
        totalCount: null,
      },
    };
  },
  watch: {
    "$route.query": function (query) {
      this.searchValue = query.keyword || "";
      this.getProductsOnSearch(query, { brandId: this.brand.id });
    },
    searchValue: _debounce(function (value) {
      this.updateURLParams("keyword", value);
      this.getProductsOnSearch(
        { ...this.$route.query, page: 1, sortBy: this.defaultSorting, keyword: value },
        { brandId: this.brand.id }
      );
    }, 500),
  },

  async beforeMount() {
    await Promise.all([this.getBrand(), this.getFavouriteProducts()]);
    await this.getBrandCategories();
    this.pageLoading = false;
    this.searchValue = this.$route.query.keyword || "";
    if (!this.$route.query.sortBy) {
      this.updateURLParams("page", 1);
      this.updateURLParams("sortBy", this.defaultSorting);
    } else {
      this.getProductsOnSearch(this.$route.query, { brandId: this.brand.id });
    }
  },

  async mounted() {
    if (this.user && this.user.role === "RETAILER") {
      this.getFullName();
    }
    trackEvent("View Content");
  },

  methods: {
    getBrand,
    fallbackGetBrandOnError,
    getFullName,
    getFilterPanelConfig,
    getBrandCategories,
  },
  computed: {
    isABrandUserOnTheirOwnBrandPage: function () {
      return this.user && this.user.role === "BRAND" && this.user.brand_vanity_url_name === this.$route.params.brandUrl;
    },

    ...mapGetters({
      user: "userState",
    }),
  },
};

async function getFullName() {
  const response = await MiscApiService.getMeRetailer();
  this.fullName = `${response.data.first_name} from ${response.data.store_name}`;
}

async function getBrand() {
  this.loadingBrand = true;

  try {
    const { data } = await BrandApiService.getBrandBySlug(this.$route.params.brandUrl);
    const brandId = data.id;

    if (this.canCheckFave) {
      const results = await FavoriteApiService.getFavoriteBrandIds();
      this.brand = data;
      this.brand.is_fav = results.data.brand_ids.includes(brandId);
    } else {
      this.brand = data;
    }
  } catch (error) {
    this.fallbackGetBrandOnError();

    if (error.response) {
      this.$swal("Attention", error.response.data.error_message);
    } else {
      this.$bugsnagClient.notify(new Error(error));
    }
  }
}

async function getBrandCategories() {
  this.searchIndex.clearCache();
  this.searchIndex
    .search({
      page: 0,
      hitsPerPage: 1,
      distinct: false,
      facets: this.defaultProductFacets,
      facetFilters: [
        ...this.$utils.constants.ALGOLIA_DEFAULT_PRODUCT_SEARCH_FACET_FILTER,
        ...[`brand.id:${this.brand.id}`],
      ],
    })
    .then((results) => {
      this.brandCategories = Object.keys(results.facets["categories.id"]);
    });
}

// Trying to acesss own outdated vanity URL.
function fallbackGetBrandOnError() {
  console.log("fallbackGetBrandOnError");
  if (this.user) {
    const vanityUrlFromCurrent = this.$router.history.current.params.brandUrl,
      vanityUrlFromStorage = this.user.brand_vanity_url_name;

    if (vanityUrlFromCurrent === vanityUrlFromStorage) {
      // Trying to acesss own outdated vanity URL.
      return BrandApiService.getPublicBrand(this.user.brand_id).then(({ data }) => {
        this.$router.replace({
          name: "brand-bio",
          params: {
            brandUrl: data.vanity_url_name,
          },
        });
        this.$session.setUserInfo({
          ...this.user,
          brand_vanity_url_name: data.vanity_url_name,
        });
        this.$store.commit("setUser", this.$session.getUserInfo());
        this.getBrand();
      });
    }
  }
}

function getFilterPanelConfig() {
  return {
    hiddenFilters: [
      FilterKeys.BRANDS.toString(),
      FilterKeys.BRAND_VALUES.toString(),
      FilterKeys.SHIP_BY.toString(),
      FilterKeys.LOCATION.toString(),
      FilterKeys.MOV.toString(),
      FilterKeys.KEYWORD.toString(),
    ],
    filterSettings: {
      [FilterKeys.CATEGORIES]: {
        categoriesFilter: this.brandCategories,
      },
    },
  };
}
</script>
